import { Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
// standards-ignore: cross-feature — IconAlertCircleFillExtraSmall is a generic affordance; a wrapper over a bare icon would add indirection without value
import { IconAlertCircleFillExtraSmall } from '@amzn/stencil-react-icons';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

/** The site-wide critical/error red. There is no `critical-text` token, so
 *  `critical-icon` is the standard critical red used for error text (matches
 *  the shared lib FormField footer, RequiredLabel, and OptionsEditor). */
const CRITICAL_TEXT_COLOR = token('color.status.critical-icon');

export interface FieldErrorTextProps {
  /** The localized error message. */
  readonly children: string;
  /** Id applied to the message `Text` so a control can point its
   *  `aria-describedby` at it. */
  readonly id?: string;
  readonly dataTestId?: string;
}

/**
 * The shared inline field-error footer: a critical-red alert-circle icon
 * followed by the message, in a `role="alert"` row — the same affordance the
 * shared lib {@link FormField} renders below a control. Used by fields that
 * can't sit inside `FormField` (rich-text editors, repeatable list editors) so
 * their error state still reads identically to the FormField-wrapped inputs.
 */
export function FieldErrorText({
  children,
  id,
  dataTestId,
}: FieldErrorTextProps): JSX.Element {
  return (
    <Row
      alignItems="center"
      gridGap="dimensions.spacing.50"
      role="alert"
      // Native data-testid (not Stencil's dataTestId prop, which emits the
      // dashed data-test-id) so RTL getByTestId resolves it.
      {...(dataTestId !== undefined ? { 'data-testid': dataTestId } : {})}
    >
      <IconAlertCircleFillExtraSmall color={CRITICAL_TEXT_COLOR} aria-hidden="true" />
      <Text
        color={CRITICAL_TEXT_COLOR}
        fontSize="T100"
        {...(id !== undefined ? { id } : {})}
      >
        {children}
      </Text>
    </Row>
  );
}
